import React, { useEffect } from "react";
import { useParams, Navigate, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import CountUp from "react-countup";
import { ArrowLeft, ArrowUpRight, MapPin, Store, Rocket, Sparkles, CheckCircle2, Download } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/Reveal";
import { BRANDS } from "@/data/content";
import { BRAND_PAGES, BRAND_STATUS_LABEL } from "@/data/brandPages";
import usePageMeta from "@/hooks/usePageMeta";
import { WhatsAppIcon, TikTokIcon, InstagramIcon } from "@/components/BrandIcons";
import BrandBlog from "@/components/BrandBlog";

// Reusable social row for any brand — solid brand-color circles, white icons.
const BrandSocial = ({ brand, lang, fill, labelColor }) => {
  if (!brand.social) return null;
  const label = lang === "ar" ? "تابعنا" : "Follow us";
  const bg = fill || brand.bg;
  const buttonBase = {
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    backgroundColor: bg,
    color: "#ffffff",
    transition: "all 0.3s ease",
  };
  return (
    <div className="flex flex-col items-center gap-4" data-testid={`brand-social-${brand.key}`}>
      <span
        className="text-xs uppercase tracking-[0.28em]"
        style={{ color: labelColor || "#8A8478" }}
      >
        {label}
      </span>
      <div className="flex items-center gap-4">
        {brand.social.instagram && (
          <a
            href={brand.social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`social-instagram-${brand.key}`}
            aria-label="Instagram"
            style={buttonBase}
            className="brand-social-btn"
          >
            <InstagramIcon className="w-[22px] h-[22px]" />
          </a>
        )}
        {brand.social.tiktok && (
          <a
            href={brand.social.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`social-tiktok-${brand.key}`}
            aria-label="TikTok"
            style={buttonBase}
            className="brand-social-btn"
          >
            <TikTokIcon className="w-[22px] h-[22px]" />
          </a>
        )}
      </div>
    </div>
  );
};

// ------------ UNDER-CONSTRUCTION LAYOUT ------------
const UnderConstruction = ({ brand, lang, t }) => {
  const isClosed = brand.status === "closed";
  const status = BRAND_STATUS_LABEL[brand.status] || BRAND_STATUS_LABEL.coming_soon;

  return (
    <section
      data-testid={`brand-page-${brand.key}`}
      className="min-h-[100svh] flex items-center relative overflow-hidden"
      style={{ backgroundColor: brand.bg, color: brand.dark ? "#1A1A1A" : "#F5F2EB" }}
    >
      <div className="absolute inset-0 opacity-10">
        <img src={brand.image} alt="" className="w-full h-full object-cover" />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 md:px-12 py-32 text-center w-full">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          {brand.logo && (
            <img
              src={brand.logo}
              alt={brand.name[lang]}
              className="max-h-40 md:max-h-56 w-auto mx-auto mb-10"
              style={{ opacity: isClosed ? 0.7 : 1 }}
            />
          )}
          <span
            className="inline-block text-xs uppercase tracking-[0.28em] px-4 py-2 border mb-8"
            style={{ borderColor: brand.accent, color: brand.accent }}
          >
            {status[lang]}
          </span>
          <h1
            className="font-display text-5xl md:text-7xl leading-tight tracking-tight mb-6"
            style={{ color: brand.dark ? "#1A1A1A" : brand.accent }}
          >
            {brand.name[lang]}
          </h1>
          {lang === "en" && (
            <p
              className="font-display text-xl md:text-2xl mb-4"
              style={{ color: brand.dark ? "#6A6A6A" : "rgba(255,255,255,0.7)" }}
            >
              {brand.name.ar}
            </p>
          )}
          <p
            className="text-lg md:text-xl max-w-2xl mx-auto mb-4 leading-relaxed"
            style={{ color: brand.dark ? "#4A4A4A" : "rgba(255,255,255,0.85)" }}
          >
            {brand.desc[lang]}
          </p>
          <p
            className="text-base mb-12 max-w-xl mx-auto"
            style={{ color: brand.dark ? "#6A6A6A" : "rgba(255,255,255,0.6)" }}
          >
            {isClosed
              ? lang === "ar"
                ? "هذه العلامة مغلقة مؤقتاً. ترقّبوا عودتها قريباً."
                : "This brand is temporarily closed. Stay tuned for its return."
              : lang === "ar"
              ? "نعمل حالياً على إطلاق صفحتها الكاملة. ترقّبوا التفاصيل قريباً."
              : "We're crafting the full experience — full details coming soon."}
          </p>
          <div className="mt-8 flex flex-col items-center gap-8">
            <Link
              to="/#brands"
              data-testid="brand-back-btn-uc"
              className="inline-flex items-center gap-2 px-6 py-3 border transition-colors hover:bg-white/10"
              style={{ borderColor: brand.accent, color: brand.accent }}
            >
              <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
              {lang === "ar" ? "عرض جميع العلامات" : "Back to All Brands"}
            </Link>
            <BrandSocial
              brand={brand}
              lang={lang}
              fill={brand.accent}
              labelColor={brand.dark ? "#4A4A4A" : "rgba(255,255,255,0.75)"}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ------------ COZ FULL PAGE ------------
const CozPage = ({ brand, lang, t }) => {
  const data = BRAND_PAGES.coz;
  return (
    <>
      {/* Hero */}
      <section
        data-testid={`brand-page-${brand.key}`}
        className="relative min-h-[80svh] flex items-center overflow-hidden"
        style={{ backgroundColor: brand.bg }}
      >
        <div className="absolute inset-0 opacity-20">
          <img src={brand.image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 text-center w-full text-[#F5F2EB]">
          <img src={brand.logo} alt={brand.name[lang]} className="max-h-44 mx-auto mb-8" />
          <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: brand.accent }}>
            {data.tagline[lang]}
          </p>
          <h1 className="font-display text-5xl md:text-7xl leading-tight tracking-tight mb-6 max-w-3xl mx-auto">
            {data.hero[lang]}
          </h1>
        </div>
      </section>

      {/* About + Vision */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12">
          <Reveal>
            <div>
              <Kicker>{lang === "ar" ? "عنّا" : "About"}</Kicker>
              <p className="text-lg text-[#4A4A4A] mt-5 leading-relaxed">{data.about[lang]}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="bg-[#2C3E3A] text-[#F5F2EB] p-10 md:p-12">
              <Kicker className="text-[#C4A27A]">{lang === "ar" ? "رؤيتنا" : "Vision"}</Kicker>
              <p className="text-lg mt-5 leading-relaxed text-[#F0E8DC]">{data.vision[lang]}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <Kicker>{data.categoriesTitle[lang]}</Kicker>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {data.categories.map((c, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="bg-[#F5F2EB] border border-[#E5DFD3] aspect-square flex items-center justify-center p-6 text-center hover:bg-[#2C3E3A] hover:text-white transition-all group">
                  <span className="font-display text-xl md:text-2xl text-[#1A1A1A] group-hover:text-white">
                    {c[lang]}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Kicker>{data.locationsTitle[lang]}</Kicker>
          <h2 className="font-display text-4xl md:text-5xl mt-4 mb-12 text-[#1A1A1A]">
            {lang === "ar" ? "فرعان رئيسيان + ٤٠ مطبخ سحابي" : "Two Flagship Branches + 40 Cloud Kitchens"}
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {data.branches.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-white border border-[#E5DFD3] p-8">
                  <Store className="w-8 h-8 text-[#6A1E27] mb-4" />
                  <div className="text-xs uppercase tracking-[0.2em] text-[#C4A27A]">{b.city[lang]}</div>
                  <div className="font-display text-2xl text-[#1A1A1A] mt-2">{b[lang]}</div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.24}>
              <div className="bg-[#2C3E3A] text-[#F5F2EB] p-8">
                <MapPin className="w-8 h-8 text-[#C4A27A] mb-4" />
                <div className="font-display text-5xl md:text-6xl leading-none">
                  <CountUp end={data.cloudKitchens.count} duration={2} enableScrollSpy scrollSpyOnce />+
                </div>
                <div className="mt-3 text-sm text-[#D8CFBE]">{data.cloudKitchens.label[lang]}</div>
                <div className="mt-4 text-xs uppercase tracking-[0.2em] text-[#C4A27A]">
                  {data.cloudKitchens.cities[lang].join(" · ")}
                </div>
              </div>
            </Reveal>
          </div>
          <div className="mt-16 flex justify-center">
            <BrandSocial brand={brand} lang={lang} />
          </div>
        </div>
      </section>
    </>
  );
};

// ------------ ZED FULL PAGE ------------
const ZedPage = ({ brand, lang, t }) => {
  const data = BRAND_PAGES.zed;
  return (
    <>
      <section
        data-testid={`brand-page-${brand.key}`}
        className="relative min-h-[80svh] flex items-center overflow-hidden"
        style={{ backgroundColor: brand.bg }}
      >
        <div className="absolute inset-0 opacity-15">
          <img src={brand.image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 w-full text-[#F5F2EB]">
          <div className="grid md:grid-cols-2 items-center gap-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: brand.accent }}>
                {data.tagline[lang]}
              </p>
              <h1 className="font-display text-5xl md:text-7xl leading-tight tracking-tight mb-6">
                {data.hero[lang]}
              </h1>
              <p className="text-lg md:text-xl max-w-xl text-[#EAF7F6] leading-relaxed">
                {data.about[lang]}
              </p>
            </div>
            <div className="flex justify-center">
              <img src={brand.logo} alt={brand.name[lang]} className="max-h-64 md:max-h-96 w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 md:py-28 bg-[#141414] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-y-12 gap-x-6">
            {data.stats.map((s, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="border-t border-white/15 pt-5">
                  <div className="font-display text-4xl md:text-5xl">
                    <CountUp end={s.value} duration={2.2} enableScrollSpy scrollSpyOnce />
                    <span className="text-[#C4A27A]">{s.suffix}</span>
                  </div>
                  <p className="mt-3 text-sm text-[#A8A296]">{s[lang]}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Franchise formats */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <Kicker>{data.franchiseTitle[lang]}</Kicker>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[#1A1A1A]">
              {data.franchiseIntro[lang]}
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {data.formats.map((f, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-white border border-[#E5DFD3] p-8 h-full flex flex-col">
                  <Rocket className="w-8 h-8 text-[#1C7A7C] mb-5" />
                  <h3 className="font-display text-2xl text-[#1A1A1A] mb-3">{f.title[lang]}</h3>
                  <p className="text-sm text-[#4A4A4A] leading-relaxed mb-6 flex-1">{f.desc[lang]}</p>
                  <div className="border-t border-[#E5DFD3] pt-5 grid grid-cols-3 gap-2 text-center">
                    <FeeBox label={lang === "ar" ? "رسوم الامتياز" : "Franchise"} value={f.fee} />
                    <FeeBox label={lang === "ar" ? "ملكية" : "Royalty"} value={f.royalty} />
                    <FeeBox label={lang === "ar" ? "تسويق" : "Marketing"} value={f.marketing} />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 text-center">
              <a
                href={`mailto:${data.franchiseEmail}`}
                data-testid="zed-franchise-cta"
                className="inline-flex items-center gap-3 bg-[#1C7A7C] text-white px-8 py-4 hover:bg-[#155A5C] transition-colors"
              >
                {lang === "ar" ? "قدّم طلب امتياز" : "Apply for Franchise"}
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <p className="text-sm text-[#8A8478] mt-4">{data.franchiseEmail}</p>
            </div>
          </Reveal>
          <div className="mt-16 flex justify-center">
            <BrandSocial brand={brand} lang={lang} />
          </div>
        </div>
      </section>

      <BrandBlog slug={brand.slug} lang={lang} accent="#1C7A7C" />
    </>
  );
};

// ------------ ZED MEALS FULL PAGE ------------
const FeeBox = ({ label, value }) => (
  <div>
    <div className="font-display text-lg text-[#6A1E27]">{value}</div>
    <div className="text-[10px] uppercase tracking-widest text-[#8A8478] mt-1">{label}</div>
  </div>
);

const ZedMealsPage = ({ brand, lang, t }) => {
  const data = BRAND_PAGES["zed-meals"];
  return (
    <>
      <section
        data-testid={`brand-page-${brand.key}`}
        className="relative min-h-[80svh] flex items-center overflow-hidden"
        style={{ backgroundColor: brand.bg }}
      >
        <div className="absolute inset-0 opacity-15">
          <img src={brand.image} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-32 w-full text-[#F5F2EB]">
          <div className="grid md:grid-cols-2 items-center gap-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: brand.accent }}>
                {data.tagline[lang]}
              </p>
              <h1 className="font-display text-5xl md:text-7xl leading-tight tracking-tight mb-6">
                {data.hero[lang]}
              </h1>
              <p className="text-lg md:text-xl max-w-xl text-[#F5E3E1] leading-relaxed mb-8">
                {data.about[lang]}
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="https://d.techrar.com/zed"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="zed-meals-app"
                  className="inline-flex items-center gap-2 bg-[#E8DAB2] text-[#8A1A26] px-6 py-3 hover:bg-white transition-colors"
                >
                  <Download className="w-4 h-4" />
                  {data.appCta[lang]}
                </a>
                <a
                  href="#zed-junior"
                  className="inline-flex items-center gap-2 border border-white/60 text-white px-6 py-3 hover:bg-white hover:text-[#8A1A26] transition-colors"
                >
                  {data.knowMore[lang]}
                </a>
              </div>
            </div>
            <div className="flex justify-center">
              <img src={brand.logo} alt={brand.name[lang]} className="max-h-72 md:max-h-96 w-auto" />
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 md:py-28 bg-[#141414] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-6">
          {data.stats.map((s, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <div className="border-t border-white/15 pt-5">
                <div className="font-display text-4xl md:text-5xl">
                  <CountUp end={s.value} duration={2.2} enableScrollSpy scrollSpyOnce />
                  <span className="text-[#E8DAB2]">{s.suffix}</span>
                </div>
                <p className="mt-3 text-sm text-[#A8A296]">{s[lang]}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values */}
      <section className="py-24 md:py-32 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <Kicker>{data.valuesTitle[lang]}</Kicker>
            <h2 className="font-display text-4xl md:text-5xl mt-4 text-[#1A1A1A]">
              {lang === "ar" ? "لماذا نحن؟" : "Why us?"}
            </h2>
          </div>
          <div className="md:col-span-7 space-y-5">
            {data.values.map((v, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-start gap-4 border-t border-[#E5DFD3] pt-5">
                  <CheckCircle2 className="w-6 h-6 text-[#8A1A26] shrink-0 mt-0.5" />
                  <p className="text-lg text-[#1A1A1A]">{v[lang]}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Coverage cities */}
      <section className="py-20 bg-white border-y border-[#E5DFD3]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Kicker>{data.citiesTitle[lang]}</Kicker>
          <div className="mt-8 flex flex-wrap gap-4">
            {data.cities[lang].map((city, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <span className="inline-flex items-center gap-2 border border-[#E5DFD3] px-5 py-3 text-lg text-[#1A1A1A]">
                  <MapPin className="w-4 h-4 text-[#8A1A26]" />
                  {city}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ZED Junior */}
      <section id="zed-junior" className="py-24 md:py-32 bg-[#8A1A26] text-[#F5F2EB] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <Sparkles className="w-10 h-10 text-[#E8DAB2] mb-6" />
          <Kicker className="text-[#E8DAB2]">{data.juniorTitle[lang]}</Kicker>
          <h2 className="font-display text-4xl md:text-6xl mt-4 mb-6 max-w-3xl leading-tight">
            {lang === "ar" ? "وجبات مدرسية صحية يحبها الأطفال" : "Healthy school meals kids actually love"}
          </h2>
          <p className="text-lg text-[#F5E3E1] max-w-2xl mb-12 leading-relaxed">{data.juniorDesc[lang]}</p>
          <div className="grid md:grid-cols-2 gap-4">
            {data.juniorFeatures.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="flex items-start gap-3 border-t border-white/15 pt-5">
                  <CheckCircle2 className="w-5 h-5 text-[#E8DAB2] shrink-0 mt-1" />
                  <p className="text-base md:text-lg">{f[lang]}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-[#F5F2EB]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <Kicker>{lang === "ar" ? "تواصل مع فريق زد ميلز" : "Talk to ZED Meals"}</Kicker>
          <div className="mt-6 flex justify-center">
            <a
              href={`https://wa.me/966${data.contact.phone1.replace(/^0/, "")}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="zed-meals-whatsapp"
              className="inline-flex items-center gap-3 border border-[#E5DFD3] px-8 py-4 hover:bg-[#25D366] hover:border-[#25D366] hover:text-white transition-colors"
            >
              <WhatsAppIcon className="w-5 h-5" />
              {data.contact.phone1}
            </a>
          </div>
          <div className="mt-14">
            <BrandSocial brand={brand} lang={lang} />
          </div>
        </div>
      </section>
    </>
  );
};

// ------------ MAIN ROUTER PAGE ------------
const BrandDetail = () => {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const brand = BRANDS.find((b) => b.slug === slug);

  useEffect(() => {
    // Instantly reset scroll on brand change (works with Lenis smooth-scroll)
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
    window.scrollTo(0, 0);
  }, [slug]);

  usePageMeta({
    title: brand
      ? `${brand.name[lang]} | ${t.company.short}`
      : t.company.name,
    favicon: brand?.logo,
  });

  if (!brand) return <Navigate to="/" replace />;

  const commonBackBtn = (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
      <Link
        to="/#brands"
        data-testid="brand-back-btn"
        className="inline-flex items-center gap-2 text-sm text-[#6A1E27] hover:gap-3 transition-all"
      >
        <ArrowLeft className="w-4 h-4 rtl:rotate-180" />
        {lang === "ar" ? "جميع العلامات" : "All Brands"}
      </Link>
    </div>
  );

  // Render based on brand
  if (brand.status !== "active") {
    return (
      <main className="pt-20">
        <UnderConstruction brand={brand} lang={lang} t={t} />
      </main>
    );
  }

  let Page = UnderConstruction;
  if (brand.slug === "coz") Page = CozPage;
  else if (brand.slug === "zed") Page = ZedPage;
  else if (brand.slug === "zed-meals") Page = ZedMealsPage;

  return (
    <main className="pt-20" data-testid={`brand-main-${brand.key}`}>
      {commonBackBtn}
      <Page brand={brand} lang={lang} t={t} />
    </main>
  );
};

export default BrandDetail;
